import { useInfiniteQuery } from '@tanstack/react-query';

import { LEGAL_AGENT_SCOPE_MAX } from '../../../components/legalAgent/legalAgentQuery';
import { get } from '../../../redux/api';
import type { LegalAgentActivityResponse } from '../../../../shared/types/routes/legalAgent';
import type { LegalAgentActor, LegalAgentQuery } from '../../../components/legalAgent/legalAgentQuery';
import type { ErrorResponse } from '../../../ReactQuery/errors';

export type ActivityScopeParams = {
  actor?: LegalAgentActor;
  projectLocators?: string[];
  releaseGroupIds?: string[];
};

/**
 * The activity route's filters for a portal query, or null when the selection is longer than the route accepts.
 * Only the active kind is sent: the route rejects both lists in one request.
 */
export function getActivityScopeParams(query: LegalAgentQuery): ActivityScopeParams | null {
  const ids = query.scope === 'releaseGroups' ? query.releaseGroups : query.projects;
  if (ids.length > LEGAL_AGENT_SCOPE_MAX) {
    return null;
  }
  //  The param names are literal keys so TypeScript checks them against ActivityScopeParams.
  const scope = query.scope === 'releaseGroups' ? { releaseGroupIds: ids } : { projectLocators: ids };
  return {
    ...(query.actor ? { actor: query.actor } : {}),
    ...(ids.length > 0 ? scope : {}),
  };
}

//  The route's default page size; it accepts up to 100.
const PAGE_SIZE = 25;

async function fetchActivityPage(params: ActivityScopeParams, before: string | undefined) {
  const response = await get<LegalAgentActivityResponse>(
    '/api/legal-agent/activity',
    false,
    { ...params, limit: PAGE_SIZE, before },
    //  Project locators hold '+' and '/'.
    { encodeQuery: true },
  );
  //  A response with no body (an HTML redirect, say) is a failure, not an empty feed.
  if (!response.data) {
    throw new Error('The activity response had no body');
  }
  return response.data;
}

/**
 * The organization's Legal Agent activity for the portal's scope and actor, newest first, a page at a time.
 * Every filter is part of the key, so a change starts again from the first page.
 */
export function useLegalAgentActivity(query: LegalAgentQuery) {
  const params = getActivityScopeParams(query);
  return useInfiniteQuery<LegalAgentActivityResponse, ErrorResponse>({
    queryKey: ['legal-agent', 'activity', params],
    queryFn: ({ pageParam }) => fetchActivityPage(params ?? {}, pageParam),
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    enabled: params !== null,
    //  A remount or a return to an earlier filter refetches the first page instead of every page loaded before.
    cacheTime: 0,
  });
}
